import { useState, useRef, useCallback, useEffect } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary, Language } from '../../../utils/dictionary';
import { FunnelSection, SalesAngle } from '../../../types/onboarding';
import { Toggle } from '../ui/Toggle';
import { GripVertical, Pencil, Image, HelpCircle, ShoppingCart, Shield, Plus, Trash2, Settings } from 'lucide-react';
import { Clock, Users, Zap, Leaf } from 'lucide-react';
import Loader from '../../common/Loader';
import { SectionTemplate } from '../../../services/funnels';

const sectionIcons: Record<string, React.ComponentType<any>> = {
  'image': Image,
  'help-circle': HelpCircle,
  'shopping-cart': ShoppingCart,
  'shield': Shield,
};

// Word-level translation map for template names
const TEMPLATE_WORD_TRANSLATIONS: Record<string, Record<string, string>> = {
  // Spanish words → { en, pt }
  'tecnologia': { en: 'Technology', pt: 'Tecnologia', es: 'Tecnología' },
  'neon': { en: 'Neon', pt: 'Neon', es: 'Neón' },
  'verde': { en: 'Green', pt: 'Verde', es: 'Verde' },
  'azul': { en: 'Blue', pt: 'Azul', es: 'Azul' },
  'rojo': { en: 'Red', pt: 'Vermelho', es: 'Rojo' },
  'negro': { en: 'Black', pt: 'Preto', es: 'Negro' },
  'blanco': { en: 'White', pt: 'Branco', es: 'Blanco' },
  'dorado': { en: 'Gold', pt: 'Dourado', es: 'Dorado' },
  'elegante': { en: 'Elegant', pt: 'Elegante', es: 'Elegante' },
  'moderno': { en: 'Modern', pt: 'Moderno', es: 'Moderno' },
  'clasico': { en: 'Classic', pt: 'Clássico', es: 'Clásico' },
  'minimalista': { en: 'Minimalist', pt: 'Minimalista', es: 'Minimalista' },
  'profesional': { en: 'Professional', pt: 'Profissional', es: 'Profesional' },
  'premium': { en: 'Premium', pt: 'Premium', es: 'Premium' },
  'natural': { en: 'Natural', pt: 'Natural', es: 'Natural' },
  'salud': { en: 'Health', pt: 'Saúde', es: 'Salud' },
  'belleza': { en: 'Beauty', pt: 'Beleza', es: 'Belleza' },
  'fitness': { en: 'Fitness', pt: 'Fitness', es: 'Fitness' },
  'deportivo': { en: 'Sport', pt: 'Esportivo', es: 'Deportivo' },
  'hogar': { en: 'Home', pt: 'Casa', es: 'Hogar' },
  'cocina': { en: 'Kitchen', pt: 'Cozinha', es: 'Cocina' },
  'moda': { en: 'Fashion', pt: 'Moda', es: 'Moda' },
  'lujo': { en: 'Luxury', pt: 'Luxo', es: 'Lujo' },
  'agresivo': { en: 'Aggressive', pt: 'Agressivo', es: 'Agresivo' },
  'suave': { en: 'Soft', pt: 'Suave', es: 'Suave' },
  'oscuro': { en: 'Dark', pt: 'Escuro', es: 'Oscuro' },
  'claro': { en: 'Light', pt: 'Claro', es: 'Claro' },
  'vibrante': { en: 'Vibrant', pt: 'Vibrante', es: 'Vibrante' },
  'secadora': { en: 'Dryer', pt: 'Secadora', es: 'Secadora' },
  'maquillaje': { en: 'Makeup', pt: 'Maquiagem', es: 'Maquillaje' },
  'rosa': { en: 'Pink', pt: 'Rosa', es: 'Rosa' },
};

/**
 * Formats a template name from API slug format to display format.
 * e.g. "preview-tecnologia-neon-verde" → "Tecnología Neón Verde" (es)
 *      "preview-tecnologia-neon-verde" → "Technology Neon Green" (en)
 */
const formatTemplateName = (name: string, lang: string): string => {
  // Remove "preview-" prefix
  let cleaned = name.replace(/^preview-/i, '');
  // Split by hyphens into words
  const words = cleaned.split('-');
  // Translate each word if available, otherwise capitalize
  const langKey = lang === 'en' ? 'en' : lang === 'pt' ? 'pt' : 'es';
  const formatted = words.map(word => {
    const translation = TEMPLATE_WORD_TRANSLATIONS[word.toLowerCase()];
    if (translation && translation[langKey]) {
      return translation[langKey];
    }
    // Fallback: capitalize first letter
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return formatted.join(' ');
};

type TabType = 'design' | 'structure' | 'angle';

const FUNNEL_COLORS = ['#6366F1', '#F97316', '#22C55E', '#EAB308', '#EC4899'];

// Fallback mock templates in case API returns empty
const FALLBACK_TEMPLATES = [
  { id: 'modern', color: 'from-[#6366F1] to-[#8B5CF6]', name: 'Moderno', description: 'Limpio y profesional' },
  { id: 'aggressive', color: 'from-[#EF4444] to-[#F97316]', name: 'Agresivo', description: 'Alto impacto visual' },
  { id: 'elegant', color: 'from-[#1E293B] to-[#475569]', name: 'Elegante', description: 'Sofisticado y premium' },
];

export function StepFunnelConfig() {
  const { language } = useAuth();
  const { multiFunnelData, updateFunnelConfig, updateFunnelSection, reorderFunnelSections, salesAngles, addFunnel, removeFunnel, sectionTemplates, isFetchingTemplates, fetchSectionTemplates, setFunnelDesign, isFetchingDesignSections } = useOnboardingStore();

  useEffect(() => {
    fetchSectionTemplates();
  }, []);
  const [activeFunnelIndex, setActiveFunnelIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<TabType>('design');
  const [editingFunnelName, setEditingFunnelName] = useState<number | null>(null);
  const [tempName, setTempName] = useState('');

  const funnels = multiFunnelData.funnels;
  const activeFunnel = funnels[activeFunnelIndex];

  if (!activeFunnel) return null;

  const tabs: { key: TabType; label: string }[] = [
    { key: 'design', label: dictionary["design-tab"]?.[language] ?? 'Diseño' },
    { key: 'structure', label: dictionary["structure-tab"]?.[language] ?? 'Estructura' },
    { key: 'angle', label: dictionary["angle-tab"]?.[language] ?? 'Ángulo' },
  ];

  const handleToggleSection = (sectionId: string, enabled: boolean) => {
    updateFunnelSection(activeFunnelIndex, sectionId, { enabled });
  };

  const handleToggleAngle = (angleId: string) => {
    const currentAngles = activeFunnel.selectedAngles;
    const isSelected = currentAngles.some(a => a.name === angleId);
    let newAngles;

    if (isSelected) {
      newAngles = [];
    } else {
      const angle = salesAngles.find(a => a.id === angleId);
      newAngles = [{ name: angleId, description: angle?.description || angleId }];
    }

    updateFunnelConfig(activeFunnelIndex, { selectedAngles: newAngles });
  };

  const startEditingName = (index: number) => {
    setEditingFunnelName(index);
    setTempName(funnels[index].name);
  };

  const saveFunnelName = () => {
    if (editingFunnelName !== null && tempName.trim()) {
      updateFunnelConfig(editingFunnelName, { name: tempName.trim() });
    }
    setEditingFunnelName(null);
  };

  const handleAddFunnel = () => {
    addFunnel();
    setActiveFunnelIndex(funnels.length); // will be the new last index
  };

  const handleRemoveFunnel = (index: number) => {
    removeFunnel(index);
    if (activeFunnelIndex >= funnels.length - 1) {
      setActiveFunnelIndex(Math.max(0, funnels.length - 2));
    } else if (activeFunnelIndex > index) {
      setActiveFunnelIndex(activeFunnelIndex - 1);
    }
  };

  const handleSelectDesign = (templateId: string, templateType: string) => {
    setFunnelDesign(activeFunnelIndex, templateId, templateType);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          {dictionary["personalize-funnels"]?.[language] ?? "Personaliza tus embudos"}
        </h1>
        <p className="text-sm text-[#64748B]">
          {dictionary["personalize-funnels-description"]?.[language] ?? "Selecciona un embudo para editar su diseño, estructura y ángulo"}
        </p>
      </div>

      <div className="flex gap-5">
        {/* Funnel sidebar */}
        <div className="w-52 flex-shrink-0 space-y-2">
          {funnels.map((funnel, index) => (
            <div
              key={funnel.id}
              className={`relative group w-full text-left px-3 py-3 rounded-2xl border transition-all cursor-pointer ${
                activeFunnelIndex === index
                  ? 'border-[#3E3EFF] bg-[#F5F8FF] shadow-sm'
                  : 'border-[#E2E8F0] bg-white hover:border-gray-300'
              }`}
              onClick={() => setActiveFunnelIndex(index)}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: FUNNEL_COLORS[index % FUNNEL_COLORS.length] }}
                >
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  {editingFunnelName === index ? (
                    <input
                      type="text"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onBlur={saveFunnelName}
                      onKeyDown={(e) => e.key === 'Enter' && saveFunnelName()}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full text-sm font-bold border-b border-[#3E3EFF] outline-none bg-transparent"
                      autoFocus
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-bold text-gray-900 truncate">{funnel.name}</p>
                      {activeFunnelIndex === index && (
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditingName(index); }}
                          className="flex-shrink-0"
                        >
                          <Pencil className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    {funnel.sections.filter(s => s.enabled).length} {dictionary["sections"]?.[language] ?? "secciones"}
                  </p>
                </div>
                {/* Delete button - only show if more than 1 funnel */}
                {funnels.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveFunnel(index); }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-red-50 transition-all flex-shrink-0"
                    title="Eliminar embudo"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add funnel button - only if < 5 */}
          {funnels.length < 5 && (
            <button
              onClick={handleAddFunnel}
              className="w-full px-3 py-3 rounded-2xl border-2 border-dashed border-[#E2E8F0] bg-[#FAFAFF] hover:border-[#3E3EFF] hover:bg-[#F5F8FF] transition-all group"
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#F1F5F9] group-hover:bg-[#EDE9FE] transition-colors flex-shrink-0">
                  <Plus className="w-4 h-4 text-gray-400 group-hover:text-[#6366F1] transition-colors" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-gray-400 group-hover:text-[#6366F1] transition-colors">
                    {dictionary["add-funnel"]?.[language] ?? "Agregar embudo"}
                  </p>
                  <p className="text-xs text-gray-300 group-hover:text-[#64748B] transition-colors">
                    {funnels.length}/5
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {/* Tabs */}
          <div className="flex bg-[#F1F5F9] rounded-2xl p-1 mb-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[420px]">
            {activeTab === 'design' && (
              <DesignTab
                activeFunnel={activeFunnel}
                onSelectDesign={handleSelectDesign}
                language={language}
                sectionTemplates={sectionTemplates}
                isFetchingTemplates={isFetchingTemplates}
              />
            )}

            {activeTab === 'structure' && (
              <StructureTab
                activeFunnel={activeFunnel}
                activeFunnelIndex={activeFunnelIndex}
                reorderFunnelSections={reorderFunnelSections}
                handleToggleSection={handleToggleSection}
                language={language}
                isFetchingDesignSections={isFetchingDesignSections}
              />
            )}

            {activeTab === 'angle' && (
              <AngleTab
                activeFunnel={activeFunnel}
                salesAngles={salesAngles}
                handleToggleAngle={handleToggleAngle}
                language={language}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Design Tab ───────────────────────────────────────────────
function DesignTab({
  activeFunnel,
  onSelectDesign,
  language,
  sectionTemplates,
  isFetchingTemplates,
}: {
  activeFunnel: any;
  onSelectDesign: (templateId: string, templateType: string) => void;
  language: Language;
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
}) {
  const hasTemplates = sectionTemplates.length > 0;
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        {dictionary["template-design"]?.[language] ?? "PLANTILLA / DISEÑO"}
      </p>

      {isFetchingTemplates ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={36} />
        </div>
      ) : hasTemplates ? (
        <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-1 py-2">
          {sectionTemplates.map((template) => {
            const templateId = template.id.toString();
            const isSelected = activeFunnel.designTemplate === templateId;
            const isHovered = hoveredId === templateId;
            const description = (template.metadata as any)?.['sub-title'] || template.type;

            return (
              <button
                key={template.id}
                onClick={() => onSelectDesign(templateId, template.type)}
                onMouseEnter={() => setHoveredId(templateId)}
                onMouseLeave={() => setHoveredId(null)}
                className={`relative w-full rounded-2xl overflow-hidden border-2 transition-shadow duration-300 ${
                  isSelected
                    ? 'border-[#3E3EFF] shadow-lg'
                    : 'border-transparent hover:border-gray-200'
                }`}
              >
                  {template.url ? (
                    <div className="w-full aspect-[3/4] bg-gray-100 overflow-hidden">
                      <img
                        src={template.url}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: isHovered ? 'center bottom' : 'center top',
                          transition: isHovered
                            ? 'object-position 15s linear'
                            : 'object-position 0.5s ease-out',
                        }}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gradient-to-b from-[#6366F1] to-[#8B5CF6] p-3 flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 mx-auto" />
                        <div className="h-1.5 bg-white/30 rounded-full w-3/4 mx-auto" />
                        <div className="h-1.5 bg-white/30 rounded-full w-1/2 mx-auto" />
                      </div>
                      <div className="space-y-1">
                        <div className="h-3 bg-white/20 rounded-md" />
                        <div className="h-3 bg-white/20 rounded-md" />
                      </div>
                    </div>
                  )}
                  {/* Title + description */}
                  <div className="px-2 py-2 bg-white">
                    <p className="text-xs font-bold text-gray-800 truncate">{formatTemplateName(template.name, language)}</p>
                    <p className="text-[10px] text-gray-400 truncate">{description}</p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
              </button>
            );
          })}
        </div>
      ) : (
        /* Fallback: show mock templates if API returned nothing */
        <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-1">
          {FALLBACK_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => onSelectDesign(template.id, template.id)}
              className={`relative rounded-2xl overflow-hidden border-2 transition-all ${
                activeFunnel.designTemplate === template.id
                  ? 'border-[#3E3EFF] shadow-lg'
                  : 'border-transparent hover:border-gray-200'
              }`}
            >
              <div className={`w-full aspect-[3/4] bg-gradient-to-b ${template.color} p-3 flex flex-col justify-between`}>
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 mx-auto" />
                  <div className="h-1.5 bg-white/30 rounded-full w-3/4 mx-auto" />
                  <div className="h-1.5 bg-white/30 rounded-full w-1/2 mx-auto" />
                </div>
                <div className="space-y-1">
                  <div className="h-3 bg-white/20 rounded-md" />
                  <div className="h-3 bg-white/20 rounded-md" />
                </div>
              </div>
              <div className="px-2 py-2 bg-white">
                <p className="text-xs font-bold text-gray-800 truncate">{template.name}</p>
                <p className="text-[10px] text-gray-400 truncate">{template.description}</p>
              </div>
              {activeFunnel.designTemplate === template.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Structure Tab with Drag & Drop ──────────────────────────
function StructureTab({
  activeFunnel,
  activeFunnelIndex,
  reorderFunnelSections,
  handleToggleSection,
  language,
  isFetchingDesignSections,
}: {
  activeFunnel: any;
  activeFunnelIndex: number;
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => void;
  handleToggleSection: (id: string, enabled: boolean) => void;
  language: Language;
  isFetchingDesignSections: boolean;
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragNode.current = e.currentTarget;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4';
    }, 0);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    setDragOverIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const sections = [...activeFunnel.sections];
      const [removed] = sections.splice(dragIndex, 1);
      sections.splice(dragOverIndex, 0, removed);
      const reordered = sections.map((s: FunnelSection, i: number) => ({ ...s, order: i }));
      reorderFunnelSections(activeFunnelIndex, reordered);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNode.current = null;
  }, [dragIndex, dragOverIndex, activeFunnel.sections, activeFunnelIndex, reorderFunnelSections]);

  const hasDesignSections = activeFunnel.sections.some((s: FunnelSection) => !s.isStandard);

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        {dictionary["funnel-structure"]?.[language] ?? "ESTRUCTURA DEL EMBUDO"}
      </p>

      {isFetchingDesignSections ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={36} />
        </div>
      ) : !hasDesignSections ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm text-gray-400 font-medium mb-1">Selecciona un diseño primero</p>
          <p className="text-xs text-gray-300">Las secciones se cargarán según el diseño elegido</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
          {activeFunnel.sections.map((section: FunnelSection, index: number) => {
            const isDragOver = dragOverIndex === index && dragIndex !== index;
            const isDesignSection = !section.isStandard;
            const IconComponent = sectionIcons[section.icon] || Settings;

            return (
              <div
                key={section.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                  isDragOver
                    ? 'border-[#3E3EFF] bg-[#F0F0FF] border-dashed'
                    : section.enabled
                      ? 'border-[#E2E8F0] bg-[#FAFAFF]'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                }`}
              >
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing flex-shrink-0" />

                {/* Thumbnail for design sections, icon for standard */}
                {isDesignSection && section.url ? (
                  <div className={`w-8 h-10 rounded-lg overflow-hidden flex-shrink-0 border ${
                    section.enabled ? 'border-[#E2E8F0]' : 'border-gray-200'
                  }`}>
                    <img
                      src={section.url}
                      alt={section.name}
                      className={`w-full h-full object-cover ${!section.enabled ? 'grayscale' : ''}`}
                    />
                  </div>
                ) : (
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    section.enabled ? 'bg-[#EDE9FE]' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-4 h-4 ${section.enabled ? 'text-[#6366F1]' : 'text-gray-400'}`} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium block truncate ${section.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                    {section.name}
                  </span>
                  {isDesignSection && section.sectionType && (
                    <span className="text-[10px] text-gray-400 truncate block">{section.sectionType}</span>
                  )}
                </div>

                <Toggle
                  enabled={section.enabled}
                  onChange={(enabled) => handleToggleSection(section.id, enabled)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Angle Tab ───────────────────────────────────────────────
function AngleTab({
  activeFunnel,
  salesAngles,
  handleToggleAngle,
  language,
}: {
  activeFunnel: any;
  salesAngles: SalesAngle[];
  handleToggleAngle: (angleId: string) => void;
  language: Language;
}) {
  const iconMap = [Clock, Users, Shield, Zap, Leaf];

  if (salesAngles.length === 0) return <Loader size={40} />;

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        {dictionary["select-angles-for-funnel"]?.[language] ?? "ÁNGULOS DE VENTA"}
      </p>
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {salesAngles.map((angle: SalesAngle, index: number) => {
          const isSelected = activeFunnel.selectedAngles.some((a: any) => a.name === angle.id);
          const Icon = angle.icon || iconMap[index % iconMap.length];

          return (
            <button
              key={angle.id}
              onClick={() => handleToggleAngle(angle.id)}
              className={`w-full px-4 py-3 rounded-[20px] border transition-all text-left ${
                isSelected
                  ? 'border-[#3e3eff] bg-[#F5F8FF]'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[#DAE4FF]' : 'bg-gray-100'
                }`}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                </div>
                <p className="text-sm font-extrabold text-gray-600 flex-1">
                  {angle.title}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
